import { ArrowTopRightOnSquareIcon, QuestionMarkCircleIcon } from "@heroicons/react/24/outline"
import { CurrencyDollarIcon, EllipsisHorizontalIcon, SparklesIcon, TagIcon } from "@heroicons/react/24/solid"
import Button from "~/components/ui/Button"
import HoverTip from "~/components/ui/HoverTip"
import ToggleLine from "~/components/ui/ToggleLine"
import { RESERVATION_KINDS, STATUSES } from "~/constants"
import IGroupedReservation from "~/interfaces/IGroupedReservation"
import stripeIntentUrlFor from "~/utils/admin/stripeIntentUrlFor"
import classNames from "~/utils/classNames"
import { useReservationInvoice } from "./useReservationInvoice"
import { checkIsCamperEmpty } from "~/components/Admin/Campers/Camper/ProfileCard"
import IPaymentMethods from "~/interfaces/IPaymentMethods"
import { formatName } from "~/utils/formatName"
import { INVOICE_STATUSES } from "~/decorators/InvoiceDecorator"
import parseDateTime from "~/utils/parseDateTime"
import InputFieldWithCurrency from "~/components/Admin/Inputs/InputFieldWithCurrency"
import { titleCase } from "~/utils"
import eAdminUserRole from "~/enums/eAdminUserRole"
import { ReservationProps } from "~/interfaces"
import Badge from "~/components/ui/Admin/Badge"
import DropdownButton from "~/components/ui/Admin/DropdownButton"
import { formatMoneyFromCents } from "~/utils/formatMoney"
import { useContext } from "react"
import { AdminContext } from "~/contexts/AdminContext"
import { TakePaymentButton } from "./TakePaymentButton"
import { EditInvoiceButton } from "./EditInvoiceButton"
import { useOrder } from "../../ReservationHooks/useOrder"
import { isMobile } from "react-device-detect"
import OrderStat from "./OrderStat"
import IOrderPreview from "~/interfaces/IOrderPreview"
import IOrder from "~/interfaces/IOrder"
import { useHistory } from "../../ReservationHooks/useHistory"
import { UseQueryResult } from "@tanstack/react-query"

interface Props {
  groupedReservation: IGroupedReservation | undefined

  orderPaymentMethods: IPaymentMethods[] | undefined
  handleUpdateAutoCharge: (v: boolean) => Promise<void>
  total_owed: string
  set_total_owed: (total_owed: string) => void
  isProcessing: boolean
  total_paid: string
  set_total_paid: (total_owed: string) => void
  isLoading: boolean
  handleMarkAsPaid: (callback: () => void) => Promise<void>
  currency: string
  setIsSlideoverOpen: (v: boolean) => void
}

const ReservationInvoice: React.FC<Props> = (props) => {
  const {
    groupedReservation,
    orderPaymentMethods,
    handleUpdateAutoCharge,
    total_owed,
    set_total_owed,
    total_paid,
    set_total_paid,
    isLoading,
    currency,
  } = props
  const { selectedReservation, camp, camper, orderInvoice, previews, currentUserRole, refetch, refetchPreview } =
    useReservationInvoice()
  const orderId = selectedReservation?.order_id || groupedReservation?.order_id
  const { data: orderData, refetch: refetchOrder } = useOrder({ orderId: orderId, includePayments: true })
  const { refetch: refetchHistory } = useHistory({ reservationId: selectedReservation?.id })
  const order = (previews?.data?.preview || orderData?.order || previews.data?.order) as (IOrder | IOrderPreview)

  const handleMarkAsPaid = async () => props.handleMarkAsPaid(() => {
    refetch()
    refetchOrder()
    refetchHistory()
    refetchPreview()
  })

  const renderAutochargeElement = () => {
    if (!camper.data?.camper || checkIsCamperEmpty(camper.data.camper)) {
      return (
        <div className="flex items-center text-gray-700">
          <CurrencyDollarIcon className="w-5 h-5 mr-2" />
          <p className="text-gray-700">Please add a guest to enable autocharge</p>
        </div>
      )
    }

    if ((orderPaymentMethods?.length ?? 0) < 1) {
      return (
        <div className="flex items-center text-gray-700">
          <CurrencyDollarIcon className="w-5 h-5 mr-2" />
          <p className="text-gray-700">
            To enable autocharge, add a payment method for {formatName(camper.data.camper)}
          </p>
        </div>
      )
    }

    return (
      <div className="flex items-center text-gray-700">
        <CurrencyDollarIcon className="w-5 h-5 mr-2" />
        <div className="flex flex-col">
          <p>Enable autocharge</p>
          {!orderInvoice?.is_attempted_auto_charge &&
            ![INVOICE_STATUSES.PAID, INVOICE_STATUSES.OVERDUE].includes(orderInvoice?.status as INVOICE_STATUSES) &&
            orderInvoice?.due_date && (
              <p className="text-sm italic text-gray-400">
                {orderInvoice?.status === INVOICE_STATUSES.DRAFT
                  ? `This invoice can be charged on ${parseDateTime(orderInvoice?.due_date)?.toFormat("LLL dd")}, but is still a draft.`
                  : `This invoice will be charged on ${parseDateTime(orderInvoice?.due_date)?.toFormat("LLL dd")}`}
              </p>
            )}
        </div>
      </div>
    )
  }

  return (
    <>
      {currentUserRole !== eAdminUserRole.ViewOnly && (
        <>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center justify-start">
              <div className="flex flex-col">
                <div className="text-xl font-bold">Invoice</div>
                <HoverTip content={"View customer invoice page"} xs>
                  <div
                    className="text-sm text-blue-500 hover:underline font-medium -mt-0.5 cursor-pointer"
                    onClick={() => {
                      window.open(`${window.origin}/i/${selectedReservation?.hashed_id}`, "_blank")
                    }}
                  >
                    #{selectedReservation?.hashed_id}
                  </div>
                </HoverTip>
              </div>
              <ReservationOrderBadge reservation={selectedReservation} className="ml-3" orderPreview={previews} />
            </div>
            <div className="flex items-center">
              <TakePaymentButton total_owed={total_owed} />
              <EditInvoiceButton setIsSlideoverOpen={props.setIsSlideoverOpen} />
              {!isMobile ? (
                <Button
                  variant="white"
                  disabled={isLoading || selectedReservation?.status === INVOICE_STATUSES.PAID}
                  className="ml-2 flex items-center text-gray-600"
                  size="small"
                  shadow="small"
                  type="button"
                  onClick={handleMarkAsPaid}
                  dataTestId="stripe-intent"
                >
                  <CurrencyDollarIcon className="h-6 w-6 text-gray-600 mr-2" aria-hidden="true" />
                  <span>Mark paid</span>
                </Button>
              ) : (
                <DropdownButton
                  disabled={isLoading}
                  options={[
                    {
                      icon: CurrencyDollarIcon,
                      label: "Mark as paid",
                      onClick: handleMarkAsPaid,
                      disabled: parseFloat(total_paid) === parseFloat(total_owed),
                    },
                  ]}
                  dataTestId="mark-as-paid"
                >
                  <Button
                    variant="white"
                    disabled={isLoading}
                    className="ml-2 flex items-center text-gray-600"
                    size="small"
                    shadow="small"
                    type="button"
                    dataTestId="mark-as-paid"
                  >
                    <EllipsisHorizontalIcon className="h-6 w-6 text-gray-600" aria-hidden="true" />
                  </Button>
                </DropdownButton>
              )}
            </div>
          </div>
          {camp.is_enabled_auto_charging && selectedReservation?.kind === RESERVATION_KINDS.RECURRING ? (
            <div className="mb-4">
              <ToggleLine
                labelClassName={classNames("pl-2 py-1 text-md font-semibold")}
                label={renderAutochargeElement()}
                checked={!!selectedReservation?.long_term_reservation?.is_enabled_auto_charging}
                onChange={handleUpdateAutoCharge}
                isRendering={previews?.isLoading}
                isDisabled={
                  !camper.data?.camper ||
                  checkIsCamperEmpty(camper.data.camper) ||
                  (orderPaymentMethods?.length ?? 0) < 1
                }
                dataTestId="autocharge"
              />
            </div>
          ) : (
            <></>
          )}
          <div className="w-full flex pb-4 items-end">
            {camp.is_admin_reservation_order_v2 ? (
              <dl className="w-full flex gap-5">
                <OrderStat
                  isLoading={previews.isLoading || !order}
                  label="Total owed"
                  valueInCents={
                    order
                      ? "unpaid_total_without_service_fee_in_cents" in order
                        ? order?.unpaid_total_without_service_fee_in_cents
                        : order?.total_in_cents
                      : null
                  }
                />
                <OrderStat isLoading={previews.isLoading || !order} label="Total paid" valueInCents={order?.paid_total_in_cents} />
                <OrderStat isLoading={previews.isLoading || !order} label="Total refunded" valueInCents={order ? order?.amount_refunded_in_cents || 0 : null} />
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => {
                      if (window.Helpkit) {
                        window.Helpkit.show("popover", "article", "4AaZYugeoTEQmgvRcRcEKW")
                      } else {
                        console.warn("Helpkit is not loaded yet.")
                      }
                    }}
                    className="absolute -top-1.5 -right-0 flex items-center h-8 rounded-lg bg-yellow-100 hover:bg-yellow-200 text-black font-medium p-1 py-0 text-xs z-10"
                  >
                    <SparklesIcon className="w-4 h-4 text-yellow-500 animate-pulse" />
                    <span className="text-md mx-2">New</span>
                  </button>
                </div>
              </dl>
            ) : (
              <>
                <div className="w-1/2">
                  <InputFieldWithCurrency
                    value={total_owed || ""}
                    type="number"
                    disabled={props.isProcessing}
                    label="Total owed"
                    name="unpaid_price_in_cents"
                    id="total_owed_in_cents"
                    onChange={(e) => set_total_owed(e.target.value)}
                    full
                    placeholder="0.00"
                    currency={currency}
                    isChanged={
                      Math.round(parseFloat(total_owed) * 100) !== Number(selectedReservation?.total_owed_in_cents)
                    }
                    dataTestId="total-owed"
                  />
                </div>

                <div className="ml-4 w-1/2">
                  <label htmlFor="name" className="block font-semibold text-md text-gray-900 mb-0.5">
                    Total paid
                  </label>
                  <div className="relative flex flex-grow items-stretch focus-within:z-10 shadow-sm">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <span className="text-gray-500 sm:text-md">$</span>
                    </div>
                    <input
                      value={total_paid || ""}
                      type="number"
                      step="any"
                      disabled={props.isProcessing}
                      name="total_paid_in_cents"
                      id="total_paid_in_cents"
                      onChange={(e) => {
                        set_total_paid(e.target.value)
                      }}
                      className={classNames(
                        "block w-full rounded-l-md border-gray-300 pl-7 xs:pr-12 focus:border-green-700 focus:ring-green-700",
                        "disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-50 disabled:text-gray-500",
                        Math.round(parseFloat(total_paid) * 100) !== Number(selectedReservation?.total_paid_in_cents)
                          ? "bg-blue-50"
                          : "bg-white",
                      )}
                      placeholder="0.00"
                      aria-describedby="price-currency"
                      data-testid="total-paid"
                    />
                    <div
                      className="pointer-events-none absolute inset-y-0 hidden xs:flex items-center pr-3"
                      style={{ right: "6.5rem" }}
                    >
                      <span className="text-gray-500 sm:text-md" id="price-currency">
                        {currency.toUpperCase()}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="relative w-44 -ml-px inline-flex justify-center items-center space-x-2 rounded-r-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm sm:text-md font-medium text-gray-600 hover:bg-gray-100 focus:border-green-700 focus:ring-green-700 focus:ring-1"
                      onClick={() => {
                        set_total_paid(total_owed.toString())
                      }}
                      data-testid="paid-in-full"
                    >
                      Paid<span className="hidden sm:inline ml-1"> in full</span>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      )}
      <div className={classNames("flex gap-4", !!selectedReservation?.order_id ? "mb-0" : "border-b pb-4 mb-6")}>
        {selectedReservation?.discount_type && (
          <div className="flex items-center justify-center rounded-md bg-gray-100 h-12 px-3 mb-4">
            <div className="mr-2">
              <TagIcon className="w-5 h-5" />
            </div>
            <div className="flex flex-col">
              <div className="flex items-center">
                <div className="font-semibold">{titleCase(selectedReservation?.discount_type)} Discount</div>
              </div>
              {selectedReservation?.discount_membership_id && (
                <div className="text-sm sm:text-md text-gray-600 -mt-1">
                  Member ID: <code>{selectedReservation?.discount_membership_id}</code>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  )
}

const ReservationOrderBadge = ({
  reservation,
  className,
  orderPreview,
}: {
  reservation: ReservationProps | null
  className?: string
  orderPreview: UseQueryResult<{
    order: IOrder;
    preview?: IOrderPreview;
  }, Error>
}) => {
  const { camp } = useContext(AdminContext)
  const orderResp = useOrder({ orderId: reservation?.order_id, includePayments: true })

  const order = orderResp?.data?.order?.status !== "paid" && orderPreview?.data?.order
    ? orderPreview?.data?.order
    : orderResp?.data?.order
  const refundedAmountInCents = Number(
    order?.payments?.reduce(
      (acc, payment) => acc + Number(payment.refunds?.reduce((acc2, refund) => acc2 + refund.amount_in_cents, 0)),
      0,
    ),
  )

  const color =
    order?.status === "paid"
      ? refundedAmountInCents > 0
        ? "green"
        : "green"
      : order?.status === "pending"
        ? "yellow"
        : "gray"
  const label =
    order?.status === "paid"
      ? refundedAmountInCents > 0
        ? `Paid - ${formatMoneyFromCents(refundedAmountInCents, camp.currency)} refunded`
        : "Paid"
      : order?.status === "pending"
        ? "Pending"
        : "Unpaid"

  return (
    <div className={className}>
      {orderPreview.isError ? (
        <div className="w-full animate-pulsing flex space-x-4"></div>
      ) : (
        <Badge color={color}>{label}</Badge>
      )}
    </div>
  )
}

export default ReservationInvoice
